import React from 'react';
import { useTheme } from '../context/ThemeContext';

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

function StagesEditor({ stages, onChange }) {
  const { darkMode } = useTheme();
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';
  const inputBg = darkMode ? '#0f0f1a' : '#fff';
  const inputBorder = darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0';

  const inputStyle = {
    background: inputBg, border: `1.5px solid ${inputBorder}`, color: text,
    padding: '12px', borderRadius: '10px', fontSize: '14px', width: '100%',
    marginBottom: 0, fontFamily: 'inherit', outline: 'none',
    colorScheme: darkMode ? 'dark' : 'light'
  };

  const labelStyle = {
    color: subtext, fontSize: '11px', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'block', marginBottom: '6px'
  };

  const addStage = () => onChange([...(stages || []), { title: '', date: '', startTime: '', endTime: '', endDate: '', time: '', description: '' }]);
  const removeStage = (i) => onChange(stages.filter((_, idx) => idx !== i));

  const updateStage = (i, field, value) => {
    const updated = [...stages];
    updated[i] = { ...updated[i], [field]: value };

    if (field === 'startTime' || field === 'endTime') {
      const start = field === 'startTime' ? value : updated[i].startTime;
      const end = field === 'endTime' ? value : updated[i].endTime;
      updated[i].time = start && end
        ? `${formatTime12(start)} - ${formatTime12(end)}`
        : start ? formatTime12(start) : '';
    }

    onChange(updated);
  };

  return (
    <div style={{ marginBottom: '14px' }}>
      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          filter: ${darkMode ? 'invert(1)' : 'none'}; cursor: pointer;
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ color: text, fontSize: '13px', fontWeight: '600' }}>
          📅 Stages / Schedule <span style={{ color: subtext, fontWeight: '400' }}>(optional)</span>
        </label>
        <button type="button" onClick={addStage} style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#fff5f7', color: '#e94560', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: '1px solid rgba(233,69,96,0.2)', cursor: 'pointer' }}>
          + Add Stage
        </button>
      </div>

      {(stages || []).map((stage, i) => (
        <div key={i} style={{ background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8f8f8', borderRadius: '12px', padding: '14px', marginBottom: '12px', border: `1px solid ${inputBorder}` }}>

          {/* Title */}
          <div style={{ marginBottom: '10px' }}>
            <label style={labelStyle}>Stage Title</label>
            <input
              placeholder="e.g. Registration, Opening Ceremony"
              value={stage.title}
              onChange={e => updateStage(i, 'title', e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Start Date + Start Time row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={labelStyle}>📅 Start Date</label>
              <input
                type="date"
                value={stage.date || ''}
                onChange={e => updateStage(i, 'date', e.target.value)}
                style={inputStyle}
              />
              {stage.date && (
                <p style={{ color: '#e94560', fontSize: '11px', marginTop: '4px', fontWeight: '600' }}>
                  {formatDate(stage.date)}
                </p>
              )}
            </div>
            <div>
              <label style={labelStyle}>🕐 Start Time</label>
              <input
                type="time"
                value={stage.startTime || ''}
                onChange={e => updateStage(i, 'startTime', e.target.value)}
                style={inputStyle}
              />
              {stage.startTime && (
                <p style={{ color: '#e94560', fontSize: '11px', marginTop: '4px', fontWeight: '600' }}>
                  {formatTime12(stage.startTime)}
                </p>
              )}
            </div>
          </div>

          {/* End Date + End Time row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={labelStyle}>📅 End Date <span style={{ color: '#e94560' }}>(if different)</span></label>
              <input
                type="date"
                value={stage.endDate || ''}
                onChange={e => updateStage(i, 'endDate', e.target.value)}
                style={inputStyle}
              />
              {stage.endDate && (
                <p style={{ color: '#e94560', fontSize: '11px', marginTop: '4px', fontWeight: '600' }}>
                  {formatDate(stage.endDate)}
                </p>
              )}
            </div>
            <div>
              <label style={labelStyle}>🕐 End Time</label>
              <input
                type="time"
                value={stage.endTime || ''}
                onChange={e => updateStage(i, 'endTime', e.target.value)}
                style={inputStyle}
              />
              {stage.endTime && (
                <p style={{ color: '#e94560', fontSize: '11px', marginTop: '4px', fontWeight: '600' }}>
                  {formatTime12(stage.endTime)}
                </p>
              )}
            </div>
          </div>

          {/* Preview */}
          {(stage.date || stage.time) && (
            <div style={{ background: darkMode ? 'rgba(233,69,96,0.08)' : '#fff5f7', border: '1px solid rgba(233,69,96,0.15)', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px', fontSize: '12px', color: '#e94560', fontWeight: '600' }}>
              📅 {stage.date ? formatDate(stage.date) : ''} {stage.time ? `• 🕐 ${stage.time}` : ''}
              {stage.endDate ? ` → ${formatDate(stage.endDate)}` : ''}
            </div>
          )}

          {/* Description + Remove */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Description (optional)</label>
              <input
                placeholder="Brief description of this stage"
                value={stage.description}
                onChange={e => updateStage(i, 'description', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                onClick={() => removeStage(i)}
                style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee', color: '#e94560', padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                ✕
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default StagesEditor;